import type { AxiosRequestConfig } from 'axios'
import type { ApiListPayload } from '@/types/api.types'
import { apiDelete, apiDeleteResult, apiGet, apiPatchResult, apiPostResult } from '@/services/api/http'
import { apiClient } from '@/services/api/axios'
import { normalizeVoiceMimeType } from '@/modules/tasks/utils/voice-format'
import type {
  CommentCursorPage,
  PaginatedActivity,
  PaginatedComments,
  PaginatedTasks,
  Task,
  TaskChecklistItem,
  TaskCreateInput,
  TaskListItem,
  TaskListParams,
  TaskReminder,
  TaskSummary,
  TaskUpdateInput,
  TaskUserRef,
} from '@/modules/tasks/types/task.types'

const emptyByStatus: TaskSummary['byStatus'] = {
  TODO: 0,
  IN_PROGRESS: 0,
  BLOCKED: 0,
  COMPLETED: 0,
  CANCELLED: 0,
}

function toListQuery(params: TaskListParams): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = {}
  const search = params.search?.trim()
  if (search) query.search = search
  if (params.scope) query.scope = params.scope
  if (params.status) query.status = params.status
  if (params.priority) query.priority = params.priority
  if (params.kind) query.kind = params.kind
  if (params.assigneeId) query.assigneeId = params.assigneeId
  if (params.createdById) query.createdById = params.createdById
  if (params.overdue) query.overdue = true
  if (params.dueToday) query.dueToday = true
  if (params.dueDateFrom) query.dueDateFrom = params.dueDateFrom
  if (params.dueDateTo) query.dueDateTo = params.dueDateTo
  query.sortBy = params.sortBy ?? 'createdAt'
  query.sortDir = params.sortDir ?? 'desc'
  query.page = params.page ?? 1
  query.pageSize = params.pageSize ?? 20
  return query
}

function cleanWriteInput(input: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue
    body[key] = typeof value === 'string' ? value.trim() : value
  }
  return body
}

export interface TaskCommentWriteExtra {
  mentionIds?: string[]
  /** `null` clears an existing voice note on edit — omit entirely to leave
   * it untouched (create always passes one or the other explicitly). */
  clientVoiceId?: string | null
  voiceDurationSec?: number | null
  voiceWaveform?: number[] | null
  /** WhatsApp-style quote-reply — create-only; the server ignores it on
   * update (a reply's target is immutable). */
  parentCommentId?: string | null
}

function commentExtraBody(extra: TaskCommentWriteExtra | undefined): Record<string, unknown> {
  if (!extra) return {}
  const body: Record<string, unknown> = {}
  if (extra.mentionIds !== undefined) body.mentionIds = extra.mentionIds
  if (extra.clientVoiceId !== undefined) body.clientVoiceId = extra.clientVoiceId
  if (extra.voiceDurationSec !== undefined) body.voiceDurationSec = extra.voiceDurationSec
  if (extra.voiceWaveform !== undefined) body.voiceWaveform = extra.voiceWaveform
  if (extra.parentCommentId !== undefined) body.parentCommentId = extra.parentCommentId
  return body
}

export const taskService = {
  list: async (params: TaskListParams = {}, config?: AxiosRequestConfig): Promise<PaginatedTasks> => {
    const payload = await apiGet<ApiListPayload<TaskListItem> & { summary: TaskSummary }>('/tasks', {
      ...config,
      params: toListQuery(params),
    })
    return {
      data: payload.items ?? [],
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
      summary: payload.summary ?? { total: payload.total, overdueCount: 0, byStatus: emptyByStatus },
    }
  },

  getById: async (id: string, config?: AxiosRequestConfig): Promise<Task> =>
    apiGet<Task>(`/tasks/${id}`, config),

  /** Header reminder — counts of my Received + My To Do (overdue / in progress / to do). */
  getReminder: async (config?: AxiosRequestConfig): Promise<TaskReminder> => {
    const payload = await apiGet<TaskReminder | null>('/tasks/reminder', config)
    return payload ?? { overdueCount: 0, inProgressCount: 0, todoCount: 0 }
  },

  /** Active users for assignee pickers — does not require users.view. */
  listAssignees: async (config?: AxiosRequestConfig): Promise<TaskUserRef[]> =>
    apiGet<TaskUserRef[]>('/tasks/assignees', config),

  create: async (input: TaskCreateInput): Promise<Task> => {
    const { data } = await apiPostResult<Task>(
      '/tasks',
      cleanWriteInput({ ...input }),
    )
    return data
  },

  update: async (id: string, input: TaskUpdateInput): Promise<Task> => {
    const { data } = await apiPatchResult<Task>(`/tasks/${id}`, cleanWriteInput({ ...input }))
    return data
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`/tasks/${id}`)
  },

  addChecklistItem: async (id: string, title: string): Promise<Task> => {
    const { data } = await apiPostResult<Task>(`/tasks/${id}/checklist`, { title: title.trim() })
    return data
  },

  updateChecklistItem: async (
    id: string,
    itemId: string,
    input: Partial<Pick<TaskChecklistItem, 'title' | 'completed' | 'position'>>,
  ): Promise<Task> => {
    const { data } = await apiPatchResult<Task>(
      `/tasks/${id}/checklist/${itemId}`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  deleteChecklistItem: async (id: string, itemId: string): Promise<Task> => {
    const { data } = await apiDeleteResult<Task>(`/tasks/${id}/checklist/${itemId}`)
    return data
  },

  listComments: async (
    id: string,
    params: { page?: number; pageSize?: number } = {},
    config?: AxiosRequestConfig,
  ): Promise<PaginatedComments> => {
    const payload = await apiGet<ApiListPayload<PaginatedComments['data'][number]>>(
      `/tasks/${id}/comments`,
      { ...config, params: { page: params.page ?? 1, pageSize: params.pageSize ?? 50 } },
    )
    return {
      data: payload.items ?? [],
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
    }
  },

  /** Messenger-shaped cursor mode — newest page first, scroll-up loads
   * older via `nextCursor`. No `page` param is what selects this mode
   * server-side (see taskService.js's listComments). */
  listCommentsCursor: async (
    id: string,
    params: { cursor?: string; pageSize?: number } = {},
  ): Promise<CommentCursorPage> => apiGet<CommentCursorPage>(`/tasks/${id}/comments`, { params }),

  addComment: async (id: string, content: string, extra?: TaskCommentWriteExtra) => {
    const { data } = await apiPostResult<PaginatedComments['data'][number]>(`/tasks/${id}/comments`, {
      content: content.trim(),
      ...commentExtraBody(extra),
    })
    return data
  },

  updateComment: async (id: string, commentId: string, content: string, extra?: TaskCommentWriteExtra) => {
    const { data } = await apiPatchResult<PaginatedComments['data'][number]>(
      `/tasks/${id}/comments/${commentId}`,
      { content: content.trim(), ...commentExtraBody(extra) },
    )
    return data
  },

  deleteComment: async (id: string, commentId: string): Promise<void> => {
    await apiDelete(`/tasks/${id}/comments/${commentId}`)
  },

  /** WhatsApp-style "opened the Discussion" — fire-and-forget, called once
   * per mount with whichever comment ids are currently loaded. */
  markCommentsSeen: async (id: string, commentIds: string[]): Promise<void> => {
    if (!commentIds.length) return
    await apiPostResult(`/tasks/${id}/comments/seen`, { commentIds })
  },

  /** Listener pressed play — marks the note opened for this viewer. */
  markVoiceOpened: async (id: string, commentId: string): Promise<void> => {
    await apiPostResult(`/tasks/${id}/comments/${commentId}/voice-opened`, {})
  },

  /** Toggle/replace my reaction on a comment — same emoji again removes it,
   * a different emoji replaces it (one reaction per user per comment,
   * enforced server-side). Returns the full comment so its recomputed
   * `reactions` can be merged straight into the cache, no extra fetch. */
  toggleReaction: async (
    id: string,
    commentId: string,
    emoji: string,
  ): Promise<PaginatedComments['data'][number]> => {
    const { data } = await apiPostResult<PaginatedComments['data'][number]>(
      `/tasks/${id}/comments/${commentId}/reactions`,
      { emoji },
    )
    return data
  },

  /** Second step of send: the stub comment (from `addComment`) already
   * exists — this attaches the actual audio. FormData must not keep the
   * axios default `application/json` Content-Type (see axios interceptor). */
  uploadCommentVoice: async (
    taskId: string,
    commentId: string,
    blob: Blob,
    mimeType: string,
  ): Promise<PaginatedComments['data'][number]> => {
    const type = normalizeVoiceMimeType(mimeType || blob.type || 'audio/webm')
    const ext = type === 'audio/ogg' ? 'ogg' : type === 'audio/mp4' ? 'm4a' : 'webm'
    const file =
      typeof File !== 'undefined'
        ? new File([blob], `voice.${ext}`, { type })
        : blob
    const form = new FormData()
    form.append('file', file, `voice.${ext}`)
    const { data } = await apiPostResult<PaginatedComments['data'][number]>(
      `/tasks/${taskId}/comments/${commentId}/voice`,
      form,
    )
    return data
  },

  /** Binary stream, not a JSON envelope — bypasses `http.ts`'s
   * envelope-unwrapping helpers on purpose (they'd choke on a raw Blob
   * response) and calls the shared, auth-header-attached `apiClient`
   * directly, same as the module-service carve-out `axios.ts` documents. */
  fetchCommentVoiceBlob: async (
    taskId: string,
    commentId: string,
  ): Promise<{ blob: Blob; mimeType: string }> => {
    const response = await apiClient.get(`/tasks/${taskId}/comments/${commentId}/voice`, {
      responseType: 'blob',
      // Let us inspect non-2xx bodies (JSON error envelopes arrive as Blobs
      // when responseType is blob — otherwise axios hides the real status).
      validateStatus: () => true,
    })
    const mimeType = (String(response.headers['content-type'] || '').split(';')[0] ?? '').trim()
    const blob = response.data as Blob
    if (response.status < 200 || response.status >= 300) {
      const err = new Error(`Voice fetch failed (${response.status})`) as Error & {
        response?: { status: number }
      }
      err.response = { status: response.status }
      throw err
    }
    // Some gateways still return 200 + JSON error as a blob — reject those.
    if (mimeType.includes('application/json') || mimeType.includes('text/')) {
      const err = new Error('Voice fetch returned a non-audio payload') as Error & {
        response?: { status: number }
      }
      err.response = { status: 404 }
      throw err
    }
    return { blob, mimeType: mimeType || blob.type || 'audio/webm' }
  },

  listActivity: async (
    id: string,
    params: { page?: number; pageSize?: number } = {},
    config?: AxiosRequestConfig,
  ): Promise<PaginatedActivity> => {
    const payload = await apiGet<ApiListPayload<PaginatedActivity['data'][number]>>(
      `/tasks/${id}/activity`,
      { ...config, params: { page: params.page ?? 1, pageSize: params.pageSize ?? 50 } },
    )
    return {
      data: payload.items ?? [],
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
    }
  },
}
